import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const prixRows = [
  { type: 'prix_row1_type', price: 'prix_row1_price' },
  { type: 'prix_row2_type', price: 'prix_row2_price' },
  { type: 'prix_row3_type', price: 'prix_row3_price' },
  { type: 'prix_row4_type', price: 'prix_row4_price' },
];

export default function Prix() {
  const { t } = useTranslation('refection');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <section ref={ref} id="prix" className="bg-soft-gray py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-3 md:mb-4">
            {t('prix_h2')}
          </h2>
          <p className="text-slate-primary/60 text-base md:text-lg max-w-2xl mx-auto">
            {t('prix_intro')}
          </p>
        </div>

        {/* Image banner */}
        <div
          className={`rounded-xl overflow-hidden mb-8 md:mb-10 transition-all duration-1000 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20newly%20completed%20asphalt%20shingle%20roof%20on%20a%20classic%20Quebec%20bungalow%20house%2C%20brand%20new%20dark%20grey%20architectural%20shingles%2C%20clean%20gutters%2C%20freshly%20installed%20roof%20in%20warm%20sunlight%2C%20blue%20sky%20background%2C%20professional%20real%20estate%20photography%20showcasing%20quality%20workmanship%2C%20no%20text%20visible&width=1000&height=350&seq=ref-prix1&orientation=landscape"
            alt="Toiture neuve récemment réfectée à Lévis avec bardeaux d'asphalte par couvreur RBQ"
            className="w-full h-48 md:h-64 object-cover object-top"
            loading="lazy"
            width="1000"
            height="350"
            width="1000"
            height="350"
          />
        </div>

        {/* Tableau desktop */}
        <div
          className={`hidden md:block bg-white rounded-xl border border-soft-dark overflow-hidden transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <table className="w-full">
            <thead>
              <tr className="bg-slate-primary">
                <th className="text-left text-white font-semibold text-sm px-6 py-4 w-2/3">
                  {t('prix_table_type')}
                </th>
                <th className="text-right text-white font-semibold text-sm px-6 py-4 w-1/3">
                  {t('prix_table_price')}
                </th>
              </tr>
            </thead>
            <tbody>
              {prixRows.map((row, idx) => (
                <tr
                  key={idx}
                  className={`border-t border-soft-dark hover:bg-soft-gray/50 transition-colors ${
                    idx % 2 === 1 ? 'bg-white' : 'bg-soft-gray/30'
                  }`}
                >
                  <td className="text-slate-primary font-medium text-sm md:text-base px-6 py-4">
                    {t(row.type)}
                  </td>
                  <td className="text-copper font-bold text-sm md:text-base px-6 py-4 text-right">
                    {t(row.price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards mobile */}
        <div className="md:hidden space-y-3">
          {prixRows.map((row, idx) => (
            <div
              key={idx}
              className={`bg-white rounded-lg border border-soft-dark p-5 transition-all duration-1000 delay-${200 + idx * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <p className="text-slate-primary font-medium text-sm mb-2">{t(row.type)}</p>
              <p className="text-copper font-bold text-lg">{t(row.price)}</p>
            </div>
          ))}
        </div>

        {/* Note */}
        <p
          className={`text-slate-primary/50 text-xs md:text-sm mt-6 text-center transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {t('prix_note')}
        </p>
      </div>
    </section>
  );
}